// src/redux/slices/invoicesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import api from "api/api";
import endpoint from "api/endpoints";

/**
 * Fetch a customer's invoices (`CustInvc`) for the Invoices & statements
 * page. Same lean shape as `returnsSlice` — no filter machinery; the page
 * derives its stat strip from the loaded rows so the tiles and the table
 * can never disagree.
 */
export const fetchInvoices = createAsyncThunk(
  "invoices/fetchInvoices",
  async (
    { userId, limit, offset, getAccessTokenSilently },
    { rejectWithValue }
  ) => {
    try {
      const token = await getAccessTokenSilently();
      const url = endpoint.GET_INVOICES_BY_USER_ID({ userId, limit, offset });
      const res = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data || {};
      return { invoices: data.items || [], hasMore: Boolean(data.hasMore) };
    } catch (err) {
      const status = err?.response?.status;
      const errorMessage =
        err?.response?.data?.error || "Failed to fetch invoices";

      if (status === 403) {
        return rejectWithValue({
          type: "FORBIDDEN",
          message: "Access forbidden: You can only view your own invoices",
          status: 403,
        });
      }
      if (status === 401) {
        return rejectWithValue({
          type: "UNAUTHORIZED",
          message: "Authentication required",
          status: 401,
        });
      }
      return rejectWithValue({
        type: "GENERAL_ERROR",
        message: errorMessage,
        status: status || 500,
      });
    }
  }
);

const initialState = {
  invoices: [],
  hasMore: false,
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed' | 'forbidden' | 'unauthorized'
  error: null,
  lastErrorType: null,
  authenticationFailed: false,
  lastFetch: null,
};

const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    clearInvoices() {
      return initialState;
    },

    /**
     * Refetch next time the page is shown — for a payment made somewhere
     * other than the Invoices page (the order detail's Pay button), which
     * would otherwise leave the row Open until the 5-minute staleness ran
     * out.
     */
    invalidateInvoices(state) {
      state.lastFetch = null;
    },

    /**
     * Flip one row to Paid In Full at once after a successful online
     * payment, before the background refetch confirms it — the same local
     * optimism as `useOrderDetails().markInvoicePaid`.
     */
    markInvoicePaid(state, action) {
      const id = String(action.payload);
      const row = state.invoices.find((inv) => String(inv.id) === id);
      if (row) {
        row.statuscode = "B";
        row.status = "Paid In Full";
        row.foreignamountunpaid = "0";
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.invoices = action.payload.invoices || [];
        state.hasMore = Boolean(action.payload.hasMore);
        state.lastFetch = Date.now();
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        const errorPayload = action.payload;
        if (errorPayload?.type === "FORBIDDEN") {
          state.status = "forbidden";
          state.authenticationFailed = true;
          state.lastErrorType = "FORBIDDEN";
          state.error = errorPayload.message;
        } else if (errorPayload?.type === "UNAUTHORIZED") {
          state.status = "unauthorized";
          state.authenticationFailed = true;
          state.lastErrorType = "UNAUTHORIZED";
          state.error = errorPayload.message;
        } else {
          state.status = "failed";
          state.lastErrorType = "GENERAL_ERROR";
          state.error = errorPayload?.message || "Failed to fetch invoices";
        }
      });
  },
});

export const { clearInvoices, invalidateInvoices, markInvoicePaid } =
  invoicesSlice.actions;

export const selectInvoices = (state) => state.invoices.invoices;
export const selectInvoicesHasMore = (state) => state.invoices.hasMore;
export const selectInvoicesStatus = (state) => state.invoices.status;
export const selectInvoicesError = (state) => state.invoices.error;

// Same policy as `selectShouldFetchReturns`: never after an auth failure; a
// failed fetch stays failed (otherwise the page's effect loops
// loading → failed → loading); else when never fetched, or stale after 5
// minutes. Recovery from `failed` is a reload or `invalidateInvoices`.
export const selectShouldFetchInvoices = (state) => {
  const { status, lastFetch, authenticationFailed } = state.invoices;
  if (
    authenticationFailed ||
    status === "forbidden" ||
    status === "unauthorized"
  ) {
    return false;
  }
  if (status === "failed") return false;
  const isStale = lastFetch && Date.now() - lastFetch > 5 * 60 * 1000;
  return (!lastFetch && status !== "loading") || isStale;
};

export default invoicesSlice.reducer;
